import { BrowserProvider, JsonRpcProvider } from "ethers";
import { useMemo } from "react";
import { type Config, useConnectorClient } from "wagmi";

function createAlchemyProvider(chainId: number) {
	const apiKey = import.meta.env.VITE_ALCHEMY_API_KEY;

	if (!apiKey) {
		throw new Error("Alchemy API key not found in environment variables");
	}

	const network = {
		chainId: chainId,
		name: getNetworkName(chainId),
	};

	return new JsonRpcProvider(
		`https://${getNetworkName(chainId)}.g.alchemy.com/v2/${apiKey}`,
		network,
	);
}

function getNetworkName(chainId: number): string {
	switch (chainId) {
		case 8453:
			return "base-mainnet";
		case 84532:
			return "base-sepolia";
		default:
			return "base-sepolia";
	}
}

export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
	const { data: client } = useConnectorClient<Config>({ chainId });

	return useMemo(() => {
		if (!client) return undefined;

		const walletProvider = new BrowserProvider(client.transport, {
			chainId: client.chain.id,
			name: client.chain.name,
		});

		/* Doing this because the underlying node from the wallet is Seqeunce's, but we use Alchemy due to some minor bugs that popup on Sequence causing 504s.
		 * Hence, reading through Alchemy's RPC, and signing through Sequence's.
		 * */
		return {
			getAddress: async () => client.account.address,
			signMessage: (message: string | Uint8Array) =>
				walletProvider
					.getSigner()
					.then((signer) => signer.signMessage(message)),
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			signTransaction: (tx: any) =>
				walletProvider.getSigner().then((signer) => signer.signTransaction(tx)),
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			signTypedData: (domain: any, types: any, _value: any) =>
				walletProvider
					.getSigner()
					.then((signer) => signer.signTypedData(domain, types, _value)),
			// biome-ignore lint/suspicious/noExplicitAny: <explanation>
			sendTransaction: (tx: any) =>
				walletProvider.getSigner().then((signer) => signer.sendTransaction(tx)),
			provider: createAlchemyProvider(client.chain.id),
		};
	}, [client]);
}
